import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DocumentService, UserService } from '@app/shared/services';
import { NotifyAPIUpdateService } from '@app/shared/services/notify-api-update/notify-api-update.service';
import { USStates } from '@app/shared/utils/enum';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { AddApiRapidValueService } from '../../add-api-rapid-value.service';
import { AddressFormMapping, GoogleMapsAutocompleteService } from '@app/shared/services/google-maps-autocomplete/google-maps-autocomplete.service';
import { debounceTime } from 'rxjs/operators';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Component({
  selector: 'add-edit-property-value',
  templateUrl: './add-edit-property-value.component.html',
  styleUrl: './add-edit-property-value.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddEditPropertyValueComponent implements OnInit, OnDestroy {
  @ViewChild('streetAddressInput', { static: false }) streetAddressInput: ElementRef;
  @Input() inlineComponent = false;
  @Input() inlineData;
  @Output() actionEvent = new EventEmitter<any>();
  @Output() updateEvent = new EventEmitter<any>();
  propertyForm: UntypedFormGroup;
  isFetchedData = false;
  is_active_connection = false;
  buttonClass = 'disable-btn';

  payload;
  is_error = false;
  currentFormId;
  dialogRef;
  autocomplete = null;
  isFetchingData = false;
  isview = false;
  receivedGeneralInformationData;
  subscription = new Subscription();
  markOwnerAsComplete = false;
  showCopyDataButton = false;
  response;
  stateList = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' },
  ];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private cd: ChangeDetectorRef,
    private toastr: ToastrService,
    private userService: UserService,
    private injector: Injector,
    @Inject(MAT_DIALOG_DATA) public data,
    private _documentService: DocumentService,
    private _notifyAPIUpdateService: NotifyAPIUpdateService,
    private googleMapsService: GoogleMapsAutocompleteService,
    private addApiRapidValueService: AddApiRapidValueService
  ) {}

  ngOnInit(): void {
    if (this.inlineComponent) {
      this.data = this.inlineData;
    } else {
      this.dialogRef = this.injector.get(MatDialogRef<AddEditPropertyValueComponent>);
    }

    this.currentFormId = this.data.currentFormId;

    this.propertyForm = this.formBuilder.group({
      StreetAddress: ['', Validators.required],
      UnitNumber: [''],
      city: ['', Validators.required],
      state: ['', Validators.required],
      propertyValue: [''],
      estimateValue: [''],
      zipCode: ['', Validators.required],
    });

    this.updateButtonState();
    this.subscription.add(
      this.propertyForm.valueChanges.subscribe(() => {
        this.updateButtonState();
      })
    );

    // if (this.data?.formSetupData && this.data.formSetupData?.status) {
    //   this.markOwnerAsComplete = this.data.formSetupData.status === 'complete' ? true : false;
    // }

    this.subscription.add(
      this.addApiRapidValueService.getPropertyDataFromGeneral(this.data?.templateId).subscribe((data) => {
        this.receivedGeneralInformationData = data;
        const propertyDataExist =
          data && Object.keys(data).length > 0 && Object.values(data).some((value) => value !== null && value !== undefined && value !== '');
        this.showCopyDataButton = propertyDataExist ? true : false;
      })
    );

    if (this.data?.is_edit) {
      this.isview = true;
      this.is_active_connection = true;
      try {
        this.propertyForm.setValue({
          StreetAddress: this.data.apiMotorValueList[0].setting.StreetAddress || '',
          UnitNumber: this.data.apiMotorValueList[0].setting?.UnitNumber || '',
          city: this.data.apiMotorValueList[0].setting.city || '',
          state: this.data.apiMotorValueList[0].setting.state || '',
          propertyValue: this.data.apiMotorValueList[0].setting.propertyValue || '',
          estimateValue: this.data.apiMotorValueList[0].setting.estimateValue || '',
          zipCode: this.data.apiMotorValueList[0].setting.zipCode || '',
        });
        this.payload = this.data.apiMotorValueList[0].payload;
        this.response = this.data.apiMotorValueList[0].response;
      } catch (error) {}
      this.isFetchedData = true;
      this.isFetchingData = false;
      this.cd.detectChanges();
    }
  }

  ngAfterViewInit() {
    this.userService
      .loadGoogleMapsApi()
      .then(() => {
        this.cleanupAutocomplete();
        this.initializeAddresseAutocomplete();
      })
      .catch((error) => console.error('Error loading Google Maps API:', error));
  }

  fetchSave() {
    this.is_error = false;

    this.isFetchingData = true;
    let address = '';

    if (this.propertyForm.controls.StreetAddress.value?.length) {
      address = address + this.propertyForm.controls.StreetAddress.value + ', ';
    }
    if (this.propertyForm.controls.UnitNumber.value?.length) {
      address = address + this.propertyForm.controls.UnitNumber.value + ', ';
    }
    if (this.propertyForm.controls.city.value?.length) {
      address = address + this.propertyForm.controls.city.value + ', ';
    }

    if (this.propertyForm.controls.state.value?.length) {
      address = address + this.propertyForm.controls.state.value + ', ';
    }

    if (this.propertyForm.controls.zipCode.value?.length) {
      address = address + this.propertyForm.controls.zipCode.value;
    }

    const data = {
      address: address,
    };
    this.subscription.add(
      this.userService.getPropertyDetails(data).subscribe((res) => {
        if ((res.status == 'error' || !res.status) && !res.zestimate) {
          this.isFetchingData = false;
          this.toastr.error('Sorry, we are not able to find that property.');
          this.is_error = true;
          this.cd.detectChanges();
          setTimeout(() => {
            this.cleanupAutocomplete();
            this.initializeAddresseAutocomplete();
          }, 0);
          return;
        }
        this.payload = data;
        this.propertyForm.controls.propertyValue.setValue(res.zestimate);
        this.response = res;
        this.isFetchedData = true;
        this.isFetchingData = false;
        this.saveEstimate();
        this.cd.detectChanges();
        try {
        } catch (error) {}
      })
    );
  }

  saveEstimate() {
    let setting = this.propertyForm.value;
    try {
      setting.estimateValue = this.setEstimateValue(this.response, setting);
    } catch (error) {}
    const data = {
      vault_user_form_id: this.currentFormId,
      type: 'zillow',
      status: 'connected',
      response: JSON.stringify(this.response),
      setting: JSON.stringify(setting),
      payload: JSON.stringify(this.payload),
    };

    if (this.data.apiMotorValueList && this.data.apiMotorValueList.length && this.data.apiMotorValueList[0] && this.data.apiMotorValueList[0].id) {
      this.subscription.add(
        this._documentService.updateVaultFormRapidIntegration(data, this.data.apiMotorValueList[0].id).subscribe((res) => {
          if (this.inlineData) {
            this.toastr.success(`Saved successfully`, '', {
              positionClass: 'toast-bottom-left',
            });
            this.actionEvent.emit({ success: true });
          } else {
            this.dialogRef.close({
              success: true,
            });
          }

          this.is_active_connection = true;
          this.cd.detectChanges();
        })
      );
    } else {
      this.subscription.add(
        this._documentService.createVaultFormRapidIntegration(data).subscribe((res) => {
          if (this.inlineData) {
            this.actionEvent.emit({ success: true });
          } else {
            this.dialogRef.close({
              success: true,
            });
          }
          this.is_active_connection = true;
          this.cd.detectChanges();
        })
      );
    }
  }

  setEstimateValue(response, setting) {
    try {
      let trim = response.data.market_value.market_value_data.find((x) => x.trim == setting.trim);
      return trim['market value'].find((x) => x.Condition == setting.condition)[setting.type];
    } catch (error) {
      return '$0';
    }
  }

  closedialog() {
    if (this.inlineData) {
      this.actionEvent.emit({ success: false });
    } else {
      this.dialogRef.close({
        success: false,
        markOwnerAsComplete: this.markOwnerAsComplete,
      });
    }
  }

  isPropertyFormValid(): boolean {
    Object.keys(this.propertyForm.controls).forEach((key) => {
      const control = this.propertyForm.get(key);
      if (control && typeof control.value === 'string') {
        control.setValue(control.value.trim(), { emitEvent: false });
      }
    });
    return this.propertyForm.valid;
  }

  updateButtonState(): void {
    if (!this.propertyForm) {
      this.buttonClass = 'disable-btn';
      return;
    }
    this.buttonClass = this.propertyForm.valid ? 'enable-btn' : 'disable-btn';
  }

  connectToPropertyDetails() {
    if (!this.isPropertyFormValid()) {
      this.propertyForm.markAllAsTouched();
      this.updateButtonState();
      this.cd.detectChanges();
      return;
    }
    this.updateButtonState();
    this.fetchSave();
  }

  disableWhiteSpaceOnFirstCharacter(event) {
    if (event.target.selectionStart === 0 && (event.keyCode === 32 || event.keyCode === 9)) {
      event.preventDefault();
    }
  }

  copyDataFromGeneralInfo() {
    if (this.receivedGeneralInformationData) {
      const data = this.receivedGeneralInformationData;
      const mappedData: any = {};
      Object.keys(data).forEach((controlName) => {
        const fieldName = controlName.toLowerCase();
        if (fieldName.includes('streetaddress')) {
          mappedData.StreetAddress = data[controlName] || '';
        } else if (fieldName.includes('city')) {
          mappedData.city = data[controlName] || '';
        } else if (fieldName.includes('state')) {
          const stateCode = (Object.entries(USStates) as [keyof typeof USStates, string][]).find(([_, value]) => value === data[controlName])?.[0];
          mappedData.state = stateCode || data[controlName] || '';
        } else if (fieldName.includes('zipcode')) {
          mappedData.zipCode = data[controlName] || '';
        }
      });
      this.propertyForm.patchValue(mappedData);
      Object.keys(mappedData).forEach((controlName) => {
        this.propertyForm.controls[controlName].markAsDirty();
      });
      this.toastr.success(`Data from general information was successfully copied to Zillow`);
      this.cd.detectChanges();
    }
  }

  initializeAddresseAutocomplete() {
    if (!this.streetAddressInput.nativeElement) {
      return;
    }

    const formMapping: AddressFormMapping = {
      street: 'StreetAddress',
      state: 'state',
      city: 'city',
      zip: 'zipCode',
    };

    // Initialize new autocomplete
    this.autocomplete = this.googleMapsService.initializeAutocomplete(
      this.streetAddressInput.nativeElement,
      this.propertyForm,
      formMapping,
      [],
      'United States',
      (address) => {
        const stateCode = address.administrative_area_level_1;
        if (stateCode) {
          this.propertyForm.get('state')?.setValue(stateCode);
        }
      }
    );
  }

  disconnectEstablishedConnection() {
    this.subscription.add(
      this._documentService.deleteVaultFormRapidIntegration(this.currentFormId).subscribe(() => {
        this.propertyForm.reset();
        this.updateButtonState();
        this.isview = false;
        this.isFetchedData = false;
        this.is_active_connection = false;
        this.is_error = false;
        this.isFetchingData = false;
        this.addApiRapidValueService.setPropertyDataFromLiveFeeds({}, this.data?.templateId);
        this.addApiRapidValueService.propertyDataSubjectData.next(null);
        this.cd.detectChanges();
      })
    );
  }

  cleanupAutocomplete() {
    if (this.autocomplete && this.streetAddressInput?.nativeElement) {
      (window as any).google.maps.event.clearInstanceListeners(this.streetAddressInput.nativeElement);
      this.autocomplete = null;
      this.cd.detectChanges();
    }
  }

  onChange() {
    this.markOwnerAsComplete = !this.markOwnerAsComplete;
    let status = this.markOwnerAsComplete ? 'complete' : 'created';
    this._notifyAPIUpdateService.sendAPIData({ status: status, subtype: 'Zillow' });
    this.updateEvent.emit({ status: status, subtype: 'Zillow' });
    this.closedialog();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
